import { FunctionComponent, useEffect, useMemo, useState } from "react";

import {
	faBuilding,
	faCheck,
	faClock,
	faEnvelope,
	faExclamation,
	faExclamationCircle,
	faPlay,
} from "@fortawesome/pro-regular-svg-icons";

import { Link, useLocation } from "react-router-dom";

import { IconDefinition } from "@fortawesome/fontawesome-common-types";

import { format } from "date-fns";

import { faLinkedinIn } from "@fortawesome/free-brands-svg-icons";

import { useTranslation } from "react-i18next";

import { BrandsIcon } from "@/components/BrandsIcon/BrandsIcon";

import { Icon } from "@/components";

import { classNames } from "@/utils/classNames";

import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { useAppSelector } from "@/redux/hooks";

import { AssignStatus, ListSourceType } from "@/enum/list.enum";

import { Checkbox } from "@/components/Checkbox/Checkbox";

import { Tooltip } from "@/components/Tooltip/Tooltip";

import { CampaignStatus } from "@/enum/campaign-status.enum";

import { ListPerson, ListPersonOtherLists } from "../../../../schema/list";
import { isDummyProspect } from "../../../../utils/columns/dummyProspect";
import { renderCurrentScoreResult } from "../../../../utils/allColumns";
import { LeadName } from "./components/LeadName";
import { OrgName } from "./components/OrgName";
import { JobTitle } from "./components/JobTitle";
import { OrgCategory } from "./components/OrgCategory";
import { PersonLocation } from "./components/PersonLocation";
import { OrgLocation } from "./components/OrgLocation";
import { PersonLanguage } from "./components/PersonLanguage";
import { PersonOnList } from "./components/PersonOnList";
import { InfoTag } from "./components/InfoTag";
import { CopywritingCampaign } from "../../../../pages/ListEditCopywriting/ListEditCopywriting";
import { DeepResearchStatusChip } from "./components/DeepResearchStatusChip";
import { getAiSnippetStatus, getCampaignAiSnippetColumnIds } from "./utils/getAiSnippetStatus";
import { useDeepResearchStatus } from "./utils/useDeepResearchStatus";
import { getInboxMessageLabel } from "./utils/getInboxMessageLabel";
import { SenderInfo } from "./components/SenderInfo";
import { getSuggestions } from "../../../../utils/column-utils";
import { replacePlaceholders } from "../../utils/replacePlaceholders";
import { LeadColorLabels } from "./components/LeadColorLabels";
import { LeadColorDots } from "./components/LeadColorDots";
import { LeadHoldTag } from "./components/LeadHoldTag";
import { PausedTag } from "./components/PausedTag";
import { CrmSyncs } from "./components/CrmSyncs";
import { ReviewCampaignTag } from "./components/ReviewCampaignTag";
import { ChatDraftsTag } from "./components/ChatDraftsTag";
import { ReviewLeadActions } from "./components/ReviewLeadActions";
import { resolveLeadCampaignConfig } from "./utils/resolveLeadCampaignConfig";

export enum Tabs {
	CHAT = "CHAT",
	CAMPAIGN = "CAMPAIGN",
	FEED = "FEED",
}

export const Lead: FunctionComponent<{
	pKey: string;
	isCollapsed?: boolean;
	isSearchModal?: boolean;
	row: ListPerson;
	handleSetCurrentLead: (index: number) => void;
	listId: number;
	allowCampaignChange?: boolean;
	setSelected?: (selected: number[]) => void;
	selected?: number[];
	/** When true, stores row.prospect.id instead of row.id in the selection array */
	selectByProspectId?: boolean;
	/** When false, the multi-select checkbox is hidden (Review to send is single-select). */
	multiSelect?: boolean;
	/** Review to send: open the "Assign to campaign" picker for this lead (replaces the legacy popover). */
	onAssignClick?: () => void;
	/** Review to send: shows the on-hover "Ignore lead" action and handles it. */
	onIgnoreClick?: () => void;
	handleTabsChange?: (value: Tabs) => void;
	handleSearchSelection?: () => void;
}> = ({
	row,
	handleSetCurrentLead,
	pKey,
	listId,
	allowCampaignChange = false,
	setSelected,
	selected,
	selectByProspectId = false,
	multiSelect = true,
	onAssignClick,
	onIgnoreClick,
	isCollapsed = false,
	isSearchModal = false,
	handleTabsChange,
	handleSearchSelection,
}) => {
	const { inbox, currentLeadIndex } = useAppSelector(listSelector);
	const { t } = useTranslation();

	// Placeholder ("SELECT" / "Person not found") rows have no real contact: they expose a
	// "Find leads" action instead and must not offer any sending/assignment affordance.
	const isFindLeadRow = isDummyProspect(row);

	// Tracker lists are signal-monitoring lists where reaching out is a secondary action, so the
	// "Create new campaign" affordance is rendered in a less visually prominent style.
	const isTrackerList = row.list?.sourceType === ListSourceType.TRACKER;

	const [organizationImageHasError, setOrganizationImageHasError] = useState(false);

	const ts = (key: string) => t(`messages.${key}`);

	useEffect(() => {
		setOrganizationImageHasError(false);
	}, [row?.prospect?.organization?.logoUrl, currentLeadIndex]);

	const [localAllowCampaignChange, setLocalAllowCampaignChange] =
		useState<boolean>(allowCampaignChange);

	const location = useLocation();

	useEffect(() => {
		if (
			(location.pathname.includes("/ready-to-send") || location.pathname.includes("/follow-up")) &&
			!allowCampaignChange
		) {
			setLocalAllowCampaignChange(true);
		}
	}, [location.pathname]);

	const initials =
		`${row.prospect?.firstName?.charAt(0) ?? ""}${row.prospect?.lastName?.charAt(0) ?? ""}` || "?";

	const score = useMemo(() => {
		if (row.prospect?.scorePersonaCategoryResult?.overallScore === "1") {
			return "P1";
		}

		if (row.prospect?.scorePersonaCategoryResult?.overallScore === "2") {
			return "P2";
		}

		if (row.prospect?.scorePersonaCategoryResult?.overallScore === "3") {
			return "P3";
		}

		if (row.prospect?.scorePersonaCategoryResult?.overallScore === "Unsure") {
			return "?";
		}

		if (row.prospect?.scorePersonaCategoryResult?.overallScore === "Disqualified") {
			return "DQ";
		}

		return "?";
	}, [row.prospect?.scorePersonaCategoryResult?.overallScore]);

	const selectedCampaignConfig: CopywritingCampaign | undefined | null = useMemo(
		() =>
			currentLeadIndex === undefined || currentLeadIndex < 0
				? undefined
				: resolveLeadCampaignConfig(row),
		[row]
	);

	const aiSnippetStatus = useMemo(() => {
		if (!selectedCampaignConfig) {
			return null;
		}

		const aiSnippetColumnIds = getCampaignAiSnippetColumnIds(
			selectedCampaignConfig.initialNodes,
			row.list?.columns
		);

		return getAiSnippetStatus(row, aiSnippetColumnIds);
	}, [selectedCampaignConfig, row]);

	const deepResearchStatus = useDeepResearchStatus(row);

	const currentCampaignConfig: CopywritingCampaign | undefined = useMemo(() => {
		if (row) {
			return (
				row.campaignConfig ||
				(row.list.campaignConfig?.[row.campaignId || ""] as CopywritingCampaign)
			);
		}

		return null;
	}, [row]);

	const currentSender = useMemo(() => {
		const lastSchedule = row?.schedules?.[row?.schedules.length - 1];

		return currentCampaignConfig?.senders?.find(
			(sender) =>
				sender.userId === lastSchedule?.userId &&
				((lastSchedule?.emailId === null && sender.type === "linkedin") ||
					(lastSchedule?.emailId !== null &&
						sender.type === "email" &&
						sender.emailAddress === lastSchedule?.email?.email))
		);
	}, [currentCampaignConfig, row]);

	const stripHtml = (html?: string) => {
		if (!html) {
			return "";
		}

		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");

		return doc.body.textContent?.trim() || "";
	};

	const lastStep = useMemo(() => {
		// Priority: scheduled (not yet sent) first, then most recent done/failed
		const scheduledEntry = row?.schedules
			?.slice()
			?.reverse()
			?.find(
				(schedule) =>
					schedule?.processingState === "scheduled" &&
					schedule?.scheduledFor !== null &&
					schedule?.publishedAt === null
			);

		const sentEntry = row?.schedules
			?.slice()
			?.reverse()
			?.find(
				(schedule) => schedule?.processingState === "done" || schedule?.processingState === "failed"
			);

		const relevantSchedule = scheduledEntry || sentEntry;
		const lastStepId = relevantSchedule?.stepId ?? 0;

		// Handle manually sent messages (stepId: "n99")
		if (lastStepId === "n99") {
			return {
				id: "n99",
				type: "manual_message",
				data: {
					level: "Manual",
					message: null,
					nodeType: "manual_message",
				},
			};
		}

		// Handle inbox-scheduled messages (stepId: null, scheduled: true)
		if (
			relevantSchedule &&
			(relevantSchedule.stepId === null || relevantSchedule.stepId === undefined)
		) {
			return {
				id: "inbox_scheduled",
				type: "inbox_message",
				data: {
					level: "Inbox",
					message: null,
					nodeType: "inbox_message",
				},
			};
		}

		const campaignId = row?.campaignId;
		const campaignConfig = row?.list?.campaignConfig?.[campaignId ?? ""];

		const lastStepWithOverridenCampaign = campaignConfig?.initialNodes?.find(
			(r: { id: string }) => r?.id === lastStepId
		);

		const lastStepNormalCampaign = row?.campaignConfig?.initialNodes?.find(
			(r: { id: string }) => r?.id === lastStepId
		);

		return lastStepWithOverridenCampaign || lastStepNormalCampaign;
	}, [currentLeadIndex, row]);

	const lastSentData = useMemo(() => {
		const conversations = row?.prospect?.conversations?.filter(
			(convo) => convo?.listPersonId === row?.id
		);

		if (!conversations || conversations.length === 0) {
			return undefined;
		}

		// Priority: scheduled (not yet sent) first, then most recent sent/failed
		// Filter all scheduled conversations, then sort by scheduledFor ascending to get the next upcoming one
		const scheduledConvos = conversations?.filter(
			(convo) =>
				(convo?.type?.includes("scheduled") && convo?.scheduled && convo?.scheduledFor !== null) ||
				// Inbox-scheduled: stepId is null and scheduled is true
				(convo?.stepId === null && convo?.scheduled === true && convo?.scheduledFor !== null)
		);

		if (scheduledConvos && scheduledConvos.length > 0) {
			const sorted = scheduledConvos.sort((a, b) => {
				const dateA = a?.scheduledFor ? new Date(a.scheduledFor).getTime() : 0;
				const dateB = b?.scheduledFor ? new Date(b.scheduledFor).getTime() : 0;

				return dateA - dateB;
			});

			return sorted[0];
		}

		// Find the most recent sent/failed conversation
		const sentConvo = conversations
			?.slice()
			?.reverse()
			?.find(
				(convo) =>
					(convo?.type?.includes("sent") && !convo?.scheduled) ||
					(convo?.type?.includes("failed") && !convo?.scheduled) ||
					// Manually sent messages: stepId is "n99"
					convo?.stepId === "n99"
			);

		return sentConvo;
	}, [row, inbox]);

	const inboxMessageLabel = useMemo(() => {
		return getInboxMessageLabel({
			lastSentData,
			reply2: row?.reply2,
			reply3: row?.reply3,
			stripHtml,
			t,
		});
	}, [lastSentData, row]);

	const suggestions = useMemo(() => {
		return getSuggestions(row) ?? [];
	}, [row]);

	return (
		<div
			key={pKey}
			className={classNames(
				"lead-item group/leadcard relative z-0 flex flex-row gap-2 hover:z-50",
				row.schedules?.[0]?.active === false ? "opacity-50" : ""
			)}
		>
			<div
				className={classNames(
					"w-full flex flex-col gap-2 border-b border-t border-t-transparent border-x border-x-transparent box-content  cursor-pointer relative overflow-hidden border-[inset] ",
					currentLeadIndex === inbox.indexOf(row)
						? "border !border-blue-action border-t-blue-action border-x-blue-action bg-brand-white"
						: "border-border-light  bg-white hover:border hover:bg-brand-white/50 hover:border-blue-action/50",
					isCollapsed ? "pl-[3px]" : "p-2"
				)}
				onClick={() => {
					if (handleSearchSelection) {
						handleSearchSelection();
					}

					handleSetCurrentLead(inbox.indexOf(row));
				}}
			>
				{!isCollapsed ? (
					<>
						<LeadColorLabels row={row} />
						<div className="flex flex-row gap-2 w-full">
							{!isFindLeadRow &&
								multiSelect &&
								((localAllowCampaignChange && selectedCampaignConfig) || isSearchModal) && (
									<div className="flex justify-center items-center">
										<Checkbox
											isChecked={
												selected?.includes(
													selectByProspectId ? (row.prospect?.id ?? -1) : row.id
												) || false
											}
											onChange={(e) => {
												e.stopPropagation();

												const selectionId = selectByProspectId ? row.prospect?.id : row.id;

												if (!selectionId) {
													return;
												}

												if (selected?.includes(selectionId)) {
													setSelected?.((selected || []).filter((id) => id !== selectionId));
												} else {
													setSelected?.([...(selected || []), selectionId]);
												}
											}}
										/>
									</div>
								)}
							<div className="flex flex-row gap-2 grow max-w-full">
								<div className="flex flex-col gap-2 justify-start shrink-0">
									{renderCurrentScoreResult({
										scoring: row.prospect?.scorePersonaCategoryResult,
										score: score ?? "",
									})}

									{selectedCampaignConfig ? (
										<SenderInfo row={row} selectedCampaignConfig={selectedCampaignConfig} />
									) : (
										<div
											className={`bg-transparent rounded-full w-4 h-4 flex flex-row items-center justify-center cursor-pointer w-[24px] h-[24px] text-white text-xxs  font-bold`}
										></div>
									)}

									<div
										className={`bg-transparent rounded-full w-4 h-2 flex flex-row items-center justify-center cursor-pointer w-[24px] h-[24px] text-white text-xxs  font-bold`}
									></div>
									<div className="self-center">
										<LeadColorDots
											row={row}
											onReviewChat={() => {
												handleSetCurrentLead(inbox.indexOf(row));
												handleTabsChange?.(Tabs.CHAT);
											}}
										/>
									</div>

									{currentSender?.type === "linkedin" && (
										<div
											className={`bg-blue-action rounded-full w-4 h-4 flex flex-row items-center justify-center cursor-pointer w-[24px] h-[24px] text-white text-xxs  font-bold`}
										>
											<BrandsIcon className="w-3 h-3" icon={faLinkedinIn as IconDefinition} />
										</div>
									)}
									{currentSender?.type === "email" && (
										<div
											className={`bg-blue-action rounded-full w-4 h-4 flex flex-row items-center justify-center cursor-pointer w-[24px] h-[24px] text-white text-xxs  font-bold`}
										>
											<Icon className="w-3 h-3" icon={faEnvelope} />
										</div>
									)}
								</div>
								<div className="flex flex-col gap-1 w-full min-w-0">
									<div className="flex flex-row gap-2 w-full max-w-full">
										<div className="flex flex-col gap-1 w-[calc(50%-4px)] max-w-1/2">
											<LeadName row={row} />
										</div>
										<div className="flex flex-col gap-1 w-[calc(50%-4px)] max-w-1/2">
											<OrgName row={row} />
										</div>
									</div>
									<div className="flex flex-row gap-2 w-full max-w-full">
										<div className="flex flex-col gap-1 w-[calc(50%-4px)] max-w-1/2">
											<JobTitle row={row} />
										</div>
										<div className="flex flex-col gap-1 w-[calc(50%-4px)] max-w-1/2">
											<OrgCategory row={row} />
										</div>
									</div>
									<div className="flex flex-row gap-2 w-full max-w-full">
										<div className="flex flex-col gap-1 w-[calc(50%-4px)] max-w-1/2 min-w-0">
											<PersonLocation row={row} />
										</div>
										<div className="flex flex-col gap-1 w-[calc(50%-4px)] max-w-1/2 min-w-0">
											<OrgLocation row={row} />
										</div>
									</div>
									<div className="flex flex-row gap-2 w-full max-w-full">
										<div className="flex flex-col gap-1 w-[calc(50%-4px)] max-w-1/2">
											<PersonLanguage row={row} />
										</div>
										<div className="flex flex-col gap-1 w-[calc(50%-4px)] max-w-1/2">
											<PersonOnList row={row} />
										</div>
									</div>
									<div className="items-start w-full flex flex-row flex-wrap gap-1">
										{deepResearchStatus && <DeepResearchStatusChip status={deepResearchStatus} />}
										<ReviewCampaignTag
											aiSnippetStatus={aiSnippetStatus}
											allowCampaignChange={localAllowCampaignChange}
											isTrackerList={isTrackerList}
											row={row}
											selectedCampaignConfig={selectedCampaignConfig}
											onAssignClick={onAssignClick}
										/>

										{row.list.id !== listId ? (
											<InfoTag
												icon={faExclamation}
												label={`Dummy`}
												style="error"
												tooltip={
													<>
														This is a lead from the list <b>{row.list.name}</b> and will be used,
														because no leads are in the current list.
													</>
												}
											/>
										) : (
											<></>
										)}
										<LeadHoldTag schedules={row.schedules} />
										{(row.list.campaignStatus === CampaignStatus.RUNNING ||
											row.list.campaignStatus === CampaignStatus.PAUSED ||
											row.list.campaignStatus === CampaignStatus.DRAFT ||
											row.list.campaignStatus === CampaignStatus.LOADING) &&
										(selectedCampaignConfig || row.campaignId) ? (
											<PausedTag row={row} />
										) : null}
										{row.assignStatus === AssignStatus.ENDED ? (
											<InfoTag icon={faCheck} label={`Finished`} style="success" />
										) : (
											<></>
										)}
										{row.assignStatus !== AssignStatus.ENDED &&
										row.schedules &&
										row.schedules?.every((schedule) => schedule.active === true) &&
										row.list.campaignStatus === CampaignStatus.RUNNING &&
										row.schedules?.length > 0 &&
										row.schedules?.find((schedule) => schedule?.processingState === "scheduled")
											?.scheduledFor ? (
											<>
												{lastStep?.type !== "conditionalNode" && (
													<>
														{lastStep?.id === "n99" || lastStep?.id === "inbox_scheduled" ? (
															inboxMessageLabel && (
																<InfoTag
																	icon={faClock}
																	label={inboxMessageLabel}
																	noIcon={true}
																	style="success"
																/>
															)
														) : (
															<InfoTag
																icon={faClock}
																label={`Step ${lastStep?.data?.level} - ${t(`outbox.${lastStep?.type}`)}`}
																noIcon={true}
																style="success"
															/>
														)}
														{lastStep?.data?.message ? (
															<InfoTag
																icon={faClock}
																label={replacePlaceholders(
																	stripHtml(lastStep?.data?.message),
																	row,
																	suggestions
																)}
																noIcon={true}
																style="success"
															/>
														) : lastSentData?.message ? (
															<InfoTag
																icon={faClock}
																label={replacePlaceholders(
																	stripHtml(lastSentData?.message),
																	row,
																	suggestions
																)}
																noIcon={true}
																style="success"
															/>
														) : (
															<></>
														)}
													</>
												)}
											</>
										) : lastStep && row.assignStatus === AssignStatus.ENDED ? (
											<>
												{lastStep?.type !== "conditionalNode" && (
													<>
														{lastStep?.id === "n99" || lastStep?.id === "inbox_scheduled" ? (
															inboxMessageLabel && (
																<InfoTag
																	icon={faClock}
																	label={inboxMessageLabel}
																	noIcon={true}
																	style="success"
																/>
															)
														) : (
															<InfoTag
																icon={faClock}
																label={` ${t(`outbox.sent_${lastStep?.type}`)}`}
																noIcon={true}
																style="success"
															/>
														)}{" "}
														{lastStep?.data?.message ? (
															<InfoTag
																icon={faClock}
																label={replacePlaceholders(
																	stripHtml(lastStep?.data?.message),
																	row,
																	suggestions
																)}
																noIcon={true}
																style="success"
															/>
														) : lastSentData?.message ? (
															<InfoTag
																icon={faClock}
																label={replacePlaceholders(
																	stripHtml(lastSentData?.message),
																	row,
																	suggestions
																)}
																noIcon={true}
																style="success"
															/>
														) : (
															<></>
														)}
													</>
												)}
											</>
										) : (
											lastSentData &&
											row.assignStatus === AssignStatus.MESSAGE_SENT &&
											inboxMessageLabel && (
												<>
													{lastSentData?.stepId === "n99" ||
													(lastSentData?.stepId === null && lastSentData?.scheduled === true) ? (
														<InfoTag
															icon={faClock}
															label={inboxMessageLabel}
															noIcon={true}
															style="success"
														/>
													) : (
														<InfoTag
															icon={faClock}
															label={t(
																`outbox.${lastSentData?.type ? lastSentData.type : `sent_${lastStep?.data?.nodeType}`}`
															)}
															noIcon={true}
															style="success"
														/>
													)}{" "}
													<InfoTag
														icon={faClock}
														label={replacePlaceholders(
															stripHtml(lastSentData?.message),
															row,
															suggestions
														)}
														noIcon={true}
														style="success"
													/>
												</>
											)
										)}
										{(row.assignStatus === AssignStatus.ASSIGNED ||
											row.assignStatus === AssignStatus.MESSAGE_SENT ||
											row.assignStatus === AssignStatus.RUNNING) &&
										row.schedules &&
										row.schedules?.every((schedule) => schedule.active === true) &&
										row.schedules?.length > 0 &&
										row.schedules?.find((schedule) => schedule?.processingState === "scheduled")
											?.scheduledFor ? (
											<InfoTag
												icon={faClock}
												label={`${
													row.schedules?.findIndex(
														(schedule) => schedule?.processingState === "scheduled"
													) === 0 ||
													row.schedules?.find(
														(schedule) => schedule?.processingState === "scheduled"
													)?.stepId === "n99"
														? "Scheduled for"
														: "Next step at"
												} ${format(
													new Date(
														row.schedules?.find(
															(schedule) =>
																schedule.scheduledFor !== null && schedule.publishedAt === null
														)?.scheduledFor || new Date()
													),
													"HH:mm dd.MM.yyyy"
												)}`}
												style="success"
											/>
										) : (
											<></>
										)}
										{row.schedules?.length &&
										row.schedules?.find((schedule) => schedule?.processingState === "scheduled")
											?.scheduledFor &&
										row.list.campaignStatus === CampaignStatus.RUNNING &&
										row.schedules?.every((schedule) => schedule.active === true) ? (
											<InfoTag icon={faPlay} label={`Running`} style="success" />
										) : (
											<></>
										)}
										{/* "Chat drafts ready" is mutually exclusive with being pre-assigned to a campaign */}
										<ChatDraftsTag row={row} onReviewChat={() => handleTabsChange?.(Tabs.CHAT)} />
										<CrmSyncs row={row} />
									</div>
								</div>
							</div>
						</div>
					</>
				) : (
					<div className="flex flex-col  items-center gap-1 py-2 w-full overflow-y-auto h-full scrollbar-hide">
						<LeadColorLabels row={row} thin />

						{renderCurrentScoreResult({
							scoring: row.prospect?.scorePersonaCategoryResult,
							score,
						})}
						<Tooltip
							content={
								`${row.prospect?.firstName ?? ""} ${row.prospect?.lastName ?? ""}`.trim() ||
								row.prospect?.name ||
								ts("unknown")
							}
						>
							<div className="bg-blue-action rounded-full flex items-center justify-center w-[24px] h-[24px] text-white text-xxs font-bold">
								{initials}
							</div>
						</Tooltip>

						<div className="flex h-[24px] w-[24px] rounded-full overflow-hidden">
							{!row.prospect?.organization?.logoUrl || organizationImageHasError ? (
								<Tooltip
									content={`${row.prospect?.organization?.name ? row.prospect?.organization?.name : ""}`}
								>
									<div className="h-[24px] w-[24px] bg-gray-200 rounded-full flex items-end justify-center">
										<Icon
											alt={row.prospect?.organization?.name ? row.prospect?.organization?.name : ""}
											className="h-[20px] w-[20px] text-blue-action"
											icon={faBuilding}
										/>
									</div>
								</Tooltip>
							) : (
								<Tooltip
									content={`${row.prospect?.organization?.name ? row.prospect?.organization?.name : ""}`}
								>
									<img
										alt={row.prospect?.organization?.name ? row.prospect?.organization?.name : ""}
										src={row.prospect?.organization?.logoUrl || ""}
										onError={() => setOrganizationImageHasError(true)}
									/>
								</Tooltip>
							)}
						</div>

						<SenderInfo
							className="w-[24px] h-[24px]"
							row={row}
							selectedCampaignConfig={selectedCampaignConfig || null}
							direction="top"
						/>
					</div>
				)}
			</div>

			{!isCollapsed && (
				<ReviewLeadActions
					isFindLeadRow={isFindLeadRow}
					isTrackerList={isTrackerList}
					row={row}
					onAssignClick={onAssignClick}
					onBeforeAssign={() => handleSetCurrentLead(inbox.indexOf(row))}
					onIgnoreClick={onIgnoreClick}
				/>
			)}
		</div>
	);
};
